import { useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown } from 'react-icons/fa';
import ClassDetailsModal from '../ClassDetailsModal';
import formatFlightFareKey from '../../../../helpers/formatters/formatFlightFareKey';

const ClassDetailsLink = ({ flightItinerary }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedFare = flightItinerary?.flightSegments?.[0]?.flights?.[0]?.selectedFare;
  const hasPriceClassDescriptions =
    selectedFare?.priceClassDescriptions && selectedFare.priceClassDescriptions.length > 0;

  const flightsList = useMemo(
    () => (flightItinerary?.flightSegments || []).flatMap((segment) => segment.flights),
    [flightItinerary],
  );

  const groupedFlights = useMemo(() => {
    const selectedFareIndex =
      flightItinerary?.flightSegments?.[0]?.flights?.[0]?.allFares?.findIndex(
        (f) => f === selectedFare || formatFlightFareKey(f) === formatFlightFareKey(selectedFare),
      );
    if (selectedFareIndex == null || selectedFareIndex < 0) return undefined;
    return flightsList.reduce((acc, flight) => {
      const currentFare = flight.allFares?.[selectedFareIndex];
      if (currentFare?.priceClassDescriptions?.length > 0) {
        const fareKey = `${currentFare.fareBrandName}-${currentFare.priceClassDescriptions.join('-')}`;
        if (!acc[fareKey]) {
          acc[fareKey] = {
            fareBrandName: currentFare.fareBrandName,
            priceClassDescriptions: currentFare.priceClassDescriptions,
            flights: [],
          };
        }
        acc[fareKey].flights.push(flight);
      }
      return acc;
    }, {});
  }, [flightsList, flightItinerary, selectedFare]);

  if (!hasPriceClassDescriptions) {
    return null;
  }

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="inline-flex items-center gap-1"
        aria-haspopup="dialog"
        aria-expanded={isModalOpen}
      >
        <span>Class details</span>
        <FaChevronDown className="inline-block" />
      </button>

      {isModalOpen && (
        <ClassDetailsModal
          priceClassDescriptions={selectedFare.priceClassDescriptions}
          groupedFlights={groupedFlights}
          onClose={handleClose}
        />
      )}
    </>
  );
};

ClassDetailsLink.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  flightItinerary: PropTypes.object.isRequired,
};

export default memo(ClassDetailsLink);
